import { find, map } from 'lodash'
import { useCallback, useEffect, useState, type FC, type ReactNode } from 'react'
import { Platform } from 'react-native'
import Purchases, {
  LOG_LEVEL,
  type CustomerInfo,
  type PurchasesStoreProduct,
  type PurchasesSubscriptionInfo
} from 'react-native-purchases'

import { RevenueCatContext } from '@/contexts'
import { useAuth } from '@/hooks'
import { supabase } from '@/lib'

const isWeb = Platform.OS === 'web'

// Only configure RevenueCat on native platforms
if (!isWeb) {
  // Use the Test Store key in debug builds only. A Test-Store key in a release
  // build crashes by design (the SDK force-closes to protect test purchases),
  // and __DEV__ is false in every release build, so a test key can never reach
  // production.
  Purchases.configure({
    apiKey: (__DEV__
      ? process.env.EXPO_PUBLIC_REVENUECAT_TEST_STORE_KEY
      : Platform.select({
          android: process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_KEY,
          ios: process.env.EXPO_PUBLIC_REVENUECAT_IOS_KEY
        })) as string
  })
}

export interface RevenueCatProviderProps {
  children: ReactNode
}

// This provider includes a mock implementation for web compatibility
// On web, it provides default values (isPlus: null, allowing db user.is_plus value to be used)
// On native platforms, it uses the full RevenueCat SDK
export const RevenueCatProvider: FC<RevenueCatProviderProps> = ({ children }) => {
  const { session } = useAuth()
  const [isPlus, setIsPlus] = useState<boolean | null>(null) // null = unknown, true = is plus, false = not plus
  const [product, setProduct] = useState<PurchasesStoreProduct | null>(null)
  const [products, setProducts] = useState<PurchasesStoreProduct[]>([])
  const [subscription, setSubscription] = useState<PurchasesSubscriptionInfo | null>(null)

  const parseCustomerInfo = useCallback(
    (customerInfo: CustomerInfo) => {
      const activeSubscription = customerInfo?.activeSubscriptions[0]
      const activeProduct = activeSubscription ? find(products, { identifier: activeSubscription }) : null

      setIsPlus(!!customerInfo?.entitlements?.active?.plus)
      setProduct(activeProduct || null)
      setSubscription(activeSubscription ? customerInfo.subscriptionsByProductIdentifier[activeSubscription] : null)

      if (session?.user?.id) {
        // The function derives the user from the JWT that supabase-js attaches
        // automatically, so no userId is sent in the body.
        supabase.functions.invoke<string>('revenuecat_sync_user', {
          method: 'POST'
        })
      }
    },
    [products, session?.user?.id]
  )

  // initialize revcat sdk once (native only)
  useEffect(() => {
    if (isWeb) {
      // Mock implementation for web - set default values
      setIsPlus(null)
      setProduct(null)
      setSubscription(null)
      setProducts([])
    } else {
      Purchases.setLogLevel(LOG_LEVEL.WARN)
        .then(Purchases.collectDeviceIdentifiers)
        .then(Purchases.getOfferings)
        .then((offerings) => setProducts(map(offerings.current?.availablePackages, 'product')))
        .catch((e) => console.error('[revcat] error', e))
    }
  }, [])

  // whenever the user information changes, be sure to update user attributes for revcat
  useEffect(() => {
    if (!isWeb) {
      ;(async () => {
        try {
          if (session?.user?.id) {
            if (__DEV__) {
              console.debug('[revcat] logged in user', session.user.id)
            }

            await Purchases.logIn(session.user.id)
          }

          if (session?.user?.email) {
            if (__DEV__) {
              console.debug('[revcat] set email', session.user.email)
            }

            await Purchases.setEmail(session.user.email)
          }

          if (session?.user?.user_metadata) {
            let displayName = ''

            if (session.user.user_metadata.name) {
              displayName = session.user.user_metadata.name
            } else if (session.user.user_metadata.full_name) {
              displayName = session.user.user_metadata.full_name
            } else if (session.user.user_metadata.fullName) {
              displayName = session.user.user_metadata.fullName
            } else if (session.user.user_metadata.given_name && session.user.user_metadata.family_name) {
              displayName = `${session.user.user_metadata.given_name} ${session.user.user_metadata.family_name}`
            } else if (session.user.user_metadata.givenName && session.user.user_metadata.familyName) {
              displayName = `${session.user.user_metadata.givenName} ${session.user.user_metadata.familyName}`
            } else if (session.user.user_metadata.first_name && session.user.user_metadata.last_name) {
              displayName = `${session.user.user_metadata.first_name} ${session.user.user_metadata.last_name}`
            } else if (session.user.user_metadata.firstName && session.user.user_metadata.lastName) {
              displayName = `${session.user.user_metadata.firstName} ${session.user.user_metadata.lastName}`
            }

            if (displayName) {
              if (__DEV__) {
                console.debug('[revcat] set display name', displayName)
              }

              await Purchases.setDisplayName(displayName)
            }
          }

          parseCustomerInfo(await Purchases.getCustomerInfo())
        } catch (e) {
          console.error('[revcat] error', e)
        }
      })()

      Purchases.addCustomerInfoUpdateListener(parseCustomerInfo)

      return () => {
        Purchases.removeCustomerInfoUpdateListener(parseCustomerInfo)
      }
    }
  }, [parseCustomerInfo, session?.user?.id, session?.user?.email, session?.user?.user_metadata])

  return <RevenueCatContext.Provider value={{ isPlus, product, subscription }}>{children}</RevenueCatContext.Provider>
}
